import { createAsyncThunk } from '@reduxjs/toolkit'
import { getKongLocationAll, getKongLocationDetail } from 'features/Location/services/location'
import {
  getBlacklistAreaDetail,
  postCreateBlacklistAddressBulk,
  deleteBlacklistAddress,
  putBlacklistAddress,
  putUpdateBlacklistAddressBulk,
  PutBlacklistAddressRequestType,
  PostCreateBlacklistAddressBulkRequestType,
  PutUpdateBlacklistAddressBulkRequestType,
} from 'features/Location/services/blacklistArea'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import { getDayAndTime } from 'utils/helpers/date'
import {
  SLICE_NAME,
  setIsLoading,
  setLocationHubOptions,
  setBlacklistDetails,
  setLocationDetail,
  resetBlacklistFormExceptLocation,
} from './slice'

export const fetchAllHub = createAsyncThunk(
  `${SLICE_NAME}/fetchServiceLevelList`,
  async (
    { location_id, name }: { location_id: number; name?: string },
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))
    const locationId = location_id ? { id: location_id } : {}
    try {
      const { data } = await getKongLocationAll({
        params: { limit: 20, page: 0, type: 'HUB', active: true, ...locationId, name },
      })

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        dispatch(
          setLocationHubOptions(
            data.data.location.map((el) => ({
              location_address: el.location_address,
              location_id: el.location_id,
              location_name: el.location_name,
              uniqueID: `${el.location_address}-${el.location_id}-${el.location_name}`,
            })),
          ),
        )
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const fetchBlacklistDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchBlacklistDetails`,
  async ({ location_id }: { location_id: string }, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await getBlacklistAreaDetail({
        location_id,
      })

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        dispatch(
          setBlacklistDetails(
            data.data.map((el) => ({
              ...el,
              start_day: getDayAndTime(el.start_date, 'day'),
              start_time: getDayAndTime(el.start_date, 'time'),
              end_day: getDayAndTime(el.end_date, 'day'),
              end_time: getDayAndTime(el.end_date, 'time'),
              file: '',
              fileName: el.address,
            })),
          ),
        )
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const createBlacklistAddressBulk = createAsyncThunk(
  `${SLICE_NAME}/createBlacklistAddressBulk`,
  async (
    { payload }: { payload: PostCreateBlacklistAddressBulkRequestType['blacklist_address'] },
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await postCreateBlacklistAddressBulk({ blacklist_address: payload })

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        toastSuccess(data.data.message)
        dispatch(resetBlacklistFormExceptLocation())
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const updateBlacklistAddressBulk = createAsyncThunk(
  `${SLICE_NAME}/updateBlacklistAddressBulk`,
  async (
    { payload, locationId }: PutUpdateBlacklistAddressBulkRequestType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await putUpdateBlacklistAddressBulk({ payload, locationId })

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        toastSuccess(data.data.message)
        dispatch(resetBlacklistFormExceptLocation())
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const removeBlacklistAddess = createAsyncThunk(
  `${SLICE_NAME}/removeBlacklistAddess`,
  async ({ blacklist_id }: { blacklist_id: string }, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await deleteBlacklistAddress({
        id: blacklist_id,
      })

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        toastSuccess(data.data.message)
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const updateBlacklistAddess = createAsyncThunk(
  `${SLICE_NAME}/updateBlacklistAddess`,
  async (payload: PutBlacklistAddressRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await putBlacklistAddress(payload)

      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        // dispatch(resetBlacklistForm())
        dispatch(resetBlacklistFormExceptLocation())
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const fetchKongLocationDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchKongLocationDetail`,
  async ({ id }: { id: string }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsLoading(true))
      const { data } = await getKongLocationDetail({ id })
      if (data.error?.status) {
        toastFailed(data.error.message)
      } else {
        //
        const { detail } = data.data
        dispatch(
          setLocationDetail({
            location_address: detail.location_address,
            location_id: detail.location_id,
            location_name: detail.location_name,
            location_type: detail.location_type,
          }),
        )
      }

      dispatch(setIsLoading(false))
      return data
    } catch (err) {
      dispatch(setIsLoading(false))
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)
